import React, { useEffect, useState } from 'react';

import MainRoutes from 'src/constants/MainRoutes';
import { ToastProvider } from 'src/hooks/useToast';
import { userEmail, refreshCustomerListAction } from 'src/store/login';
import { FETCH_PERMISSIONS, GET_ICP_FILTERS } from 'src/constants/httpEndPoints';
import { useSelector, useDispatch } from 'react-redux';
import { useFeatureFlags } from 'src/hooks/useFeatureFlags';
import { apiClient } from 'src/api/apiClient';
import { FeaturesProvider } from 'src/hooks/useFeatures';
import { createICPFiltersKeyValueHash } from 'src/store/login/create-icp-filters-key-value-hash';
import { useLocation } from 'react-router-dom';
import { getCookie } from 'src/utils/cookies';
import { getRouteTitle } from 'src/utils/routeTitle';
import { hideNavbarOnChildRoute } from 'src/utils';
import { ThemeProvider } from '@mui/material';
import theme from './hoc/theme';
import AskPaulaButtonV3 from 'src/components/AskPaulaV3';
import { useProfile } from 'src/hooks/useProfile';
import { setRolePermissions } from 'src/store/slices/permissions';
import { NavbarV2 } from 'src/components/Navbar/NavbarV2';
import { fetchWaterfallSources } from 'src/store/slices/waterfallSources';
import IntercomMessenger from 'src/components/IntercomMessenger/IntercomMessenger';
import { useVersionCheck } from 'src/hooks/useVersionCheck';
import UpdateNotification from 'src/components/UpdateNotification';
import { isInIframe } from 'src/utils/iframeSecurity.utils';
import { useSelector as useReduxSelector } from 'react-redux';
import { FloatingThemeToggle } from 'src/components/FloatingThemeToggle';
import TopProgressBar from 'src/components/TopProgressBar';

const publicRoutes = new Set([
  'login',
  'signup',
  'forgot-password',
  'set',
  'active',
  'verify',
  'login-with-password',
  'magic-link',
  'password-success',
  'set-password-success',
  'forgot-password-success',
]);

export const App: React.FC = () => {
  const dispatch = useDispatch<any>();
  const emailID = useSelector(userEmail);
  const location = useLocation();
  const featureFlags = useFeatureFlags();

  const { pathname } = useLocation();
  const [activePath, setActivePath] = useState('');

  let themeMode = useReduxSelector((state: any) => state.theme?.mode || 'light');
  let isPublicRoute = publicRoutes.has(activePath);

  // comment these to use theme toggle on public routes
  themeMode = 'light';
  isPublicRoute = false;

  // Version check for update notifications
  const { updateAvailable, currentVersion, newVersion, reloadApp, dismissUpdate, isDismissed } =
    useVersionCheck({
      checkInterval: 5 * 60 * 1000, // Check every 5 minutes
      enabled: true,
    });

  const { emailId, customerId } = useProfile();

  // Load third-party scripts only when the user is genuinely authenticated.
  // Guard on both emailID (Redux state) AND the auth cookie so stale Redux state
  // from a previous session doesn't trigger these loads on the /login page.
  useEffect(() => {
    const token = getCookie('user');
    if (!emailID || !token) return;

    if (!document.querySelector('script[src*="pendo.io"]')) {
      const pendoScript = document.createElement('script');
      pendoScript.async = true;
      pendoScript.src =
        'https://cdn.pendo.io/agent/static/1a6a9aac-3c8a-4869-7f05-eff75c0d8e9a/pendo.js';
      document.head.appendChild(pendoScript);
    }

    if (!document.querySelector('script[src*="reply.io"]')) {
      const replyScript = document.createElement('script');
      replyScript.type = 'module';
      replyScript.src = 'https://run.reply.io/dest/webcomponent.js';
      document.head.appendChild(replyScript);
    }
  }, [emailID]);

  let timeOutId: any;
  React.useEffect(() => {
    if (emailID) {
      const token = getCookie('user');
      if (token && emailID) {
        dispatch(refreshCustomerListAction({ email: emailID }));
        getWaterfallSources();
        if (timeOutId) clearTimeout(timeOutId);
        timeOutId = setTimeout(() => {
          // fetch ICP Filters data
          createFiltersKeyValueHash();
        }, 4000);
      }
      if (token) {
        fetchPermissions();
      }
    }
  }, []);

  const fetchPermissions = () => {
    const url = `${FETCH_PERMISSIONS}`;
    apiClient
      .post(url, {
        uniqueId: customerId,
        email: emailId,
      })
      .then((res) => {
        const role = res.data;
        if (role) {
          const { permission } = role;
          const payload: Record<string, string[] | boolean> = {};
          permission.forEach((permission: any) => {
            payload[permission.id] = permission.permissionValue;
          });

          dispatch(setRolePermissions(payload));
        }
      });
  };

  const getWaterfallSources = () => {
    dispatch(fetchWaterfallSources({ emailID, customerId }));
  };

  const createFiltersKeyValueHash = async () => {
    const hash: { [key: string]: { filterName: string } } = {};
    await apiClient
      .get(`${GET_ICP_FILTERS}?entity=account&customerId=${customerId}`, {
        headers: {
          'X-Login-Email': emailID,
        },
      })
      .then((res) => {
        res.data.forEach((filter: any) => {
          hash[filter.filterKey] = { filterName: filter.FilterName };
          if (filter.filterGroupKey) {
            hash[filter.filterGroupKey] = { filterName: filter.filterGroup };
          }
        });
      })
      .catch((err) => {
        console.error(err);
      });
    await apiClient
      .get(`${GET_ICP_FILTERS}?entity=persona&customerId=${customerId}`, {
        headers: {
          'X-Login-Email': emailID,
        },
      })
      .then((res) => {
        res.data.forEach((filter: any) => {
          hash[filter.filterKey] = { filterName: filter.FilterName };
          if (filter.filterGroupKey) {
            hash[filter.filterGroupKey] = { filterName: filter.filterGroup };
          }
        });
      })
      .catch((err) => {
        console.error(err);
      });
    dispatch(createICPFiltersKeyValueHash(hash));
  };

  useEffect(() => {
    setActivePath(pathname?.split('/')?.[1] || '');
  }, [pathname]);

  // Apply theme to document root
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', themeMode);
  }, [themeMode]);

  // key is base route name and value is activePath for navbar to active particular tab in left sidebar/navbar
  const showNavbarHash: { [key: string]: string } = {
    'target-profiles': 'target-profiles',
    'run-campaign': 'run-campaign',
    website: 'website',
    settings: 'settings',
    'database-search': 'database-search',
    'exclusion-list': 'settings',
    dashboard: 'dashboard',
    'run-campaign-rasp': 'run-campaign-rasp',
    'champions-tracking': 'champions-tracking',
    'account-details': 'account-details',
    'contact-details': 'contact-details',
    hpm: 'hpm',
    integrate: 'integrate',
    'edit-mappings': 'edit-mappings',
    playbook: 'playbook',
    templates: 'templates',
    opportunities: 'opportunities',
  };

  const showAskPaulaHash: { [key: string]: string } = {
    'target-profiles': 'target-profiles',
    website: 'website',
    // 'database-search': 'database-search',
    playbook: 'playbook',
    templates: 'templates',
  };

  useEffect(() => {
    const routeTitle = getRouteTitle(location.pathname);
    document.title = routeTitle;
  }, [location]);

  const conditionToShowNavbar =
    showNavbarHash[activePath || ''] && hideNavbarOnChildRoute(pathname) && !isInIframe();

  // Check if current route is a public route

  return (
    <ThemeProvider theme={theme}>
      <TopProgressBar />
      <IntercomMessenger />
      {/* Update notification for new version */}
      <UpdateNotification
        open={updateAvailable && !isDismissed}
        currentVersion={currentVersion}
        newVersion={newVersion}
        onUpdate={reloadApp}
        onDismiss={dismissUpdate}
      />
      {featureFlags.chatbot && activePath in showAskPaulaHash && <AskPaulaButtonV3 />}
      {isPublicRoute && <FloatingThemeToggle />}
      <div className="sprouts app-container app">
        <ToastProvider>
          <FeaturesProvider>
            {conditionToShowNavbar && <NavbarV2 activePath={showNavbarHash[activePath] || ''} />}
            <div
              className="app-content"
              style={{
                marginLeft: conditionToShowNavbar ? '60px' : '0',
                width: conditionToShowNavbar ? 'calc(100% - 60px)' : '100%',
                minHeight: '100vh',
                overflow: 'auto',
              }}
            >
              <MainRoutes />
            </div>
          </FeaturesProvider>
        </ToastProvider>
      </div>
    </ThemeProvider>
  );
};
